import { useState } from 'react'
import { MessageCircle, RefreshCw, Send, CheckCircle2 } from 'lucide-react'
import {
  getAdminSupport, getAdminSupportHistory, adminSupportReply, adminSupportClose,
} from '../../services/api'
import {
  C, Card, Row, Btn, Chips, BigStat, Loading, ErrorLine, Empty, inputStyle,
  useAsync, fmtDateTime, userLabel, Pill, divider, confirmAsync,
} from './adminUI'

// Раздел «Поддержка»: диалоги юзеров прямо в админке (не надо переключаться
// в чат бота). Ответ уходит юзеру в его чат с ботом тем же путём, что и ответ
// из Telegram, и пишется в историю переписки.
// «Ждут ответа» — диалоги, где последнее сообщение от юзера: это очередь.
// «Просрочено» — юзер ждёт ответа дольше суток: такие заявки подсвечиваются
// и выносятся отдельной вкладкой, чтобы ничего не потерялось.

// Порог просрочки: юзер ждёт ответа больше этого времени — заявка «просрочена».
const OVERDUE_MS = 24 * 3600 * 1000

// «YYYY-MM-DD HH:MM:SS» (UTC) из SQLite → timestamp (мс).
function sqlUtcTs(s) {
  if (!s) return NaN
  const t = Date.parse(String(s).replace(' ', 'T') + 'Z')
  return Number.isFinite(t) ? t : NaN
}

const isOverdue = (r) => {
  if (r.status !== 'open' || r.lastSender !== 'user') return false
  const ts = sqlUtcTs(r.last_message_at)
  return Number.isFinite(ts) && Date.now() - ts > OVERDUE_MS
}

// Шаблоны частых ответов: чтобы не печатать одно и то же с телефона.
const TEMPLATES = [
  { key: 'checking', label: 'Проверяю', text: 'Здравствуйте! Принял обращение, проверяю и вернусь с ответом.' },
  { key: 'payfound', label: 'Платёж найден', text: 'Платёж нашли и зачислили, подписка активна. Извините за задержку!' },
  { key: 'paywait', label: 'Ждём сеть', text: 'Платёж виден в сети, но ещё не набрал подтверждений. Как только пройдёт — подписка включится автоматически.' },
  { key: 'keys', label: 'Про ключи', text: 'Проверьте, что у API-ключа включены права на торговлю фьючерсами и снят IP-фильтр. Инструкция есть в разделе «Настройки → Инструкция».' },
  { key: 'done', label: 'Решено', text: 'Вопрос решён. Если что-то ещё понадобится — напишите, мы на связи.' },
]

export default function SupportSection() {
  const [status, setStatus] = useState('open')
  const [openUser, setOpenUser] = useState(null)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')

  const list = useAsync(() => getAdminSupport({ status, limit: 60 }), [status])
  const hist = useAsync(
    () => (openUser ? getAdminSupportHistory(openUser) : Promise.resolve(null)),
    [openUser],
  )

  const stats = list.data?.stats || {}
  const by = list.data?.byStatus || {}
  const rows = list.data?.rows || []
  const messages = (hist.data?.messages || []).slice().reverse() // бэкенд отдаёт новые первыми

  const send = async () => {
    const t = text.trim()
    if (!openUser || !t) return
    setBusy(true); setNote('')
    try {
      const r = await adminSupportReply(openUser, t)
      setNote(r?.delivered ? 'Ответ отправлен юзеру' : 'Ответ сохранён, но доставить в Telegram не удалось (нет BOT_TOKEN или юзер заблокировал бота)')
      setText('')
      hist.reload()
      list.reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(false) }
  }

  const close = async () => {
    if (!openUser) return
    if (!(await confirmAsync(`Закрыть диалог с юзером ${openUser}?`))) return
    setBusy(true); setNote('')
    try {
      await adminSupportClose(openUser)
      setNote(`Диалог с ${openUser} закрыт`)
      setOpenUser(null)
      list.reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(false) }
  }

  return (
    <div>
      <Card
        title="Поддержка"
        icon={MessageCircle}
        right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={list.reload}>Обновить</Btn>}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <BigStat label="Ждут ответа" value={stats.waitingAnswer ?? 0} color={stats.waitingAnswer ? C.warn : C.dim} />
          <BigStat label="Просрочено &gt;1 сут" value={stats.overdue ?? 0} color={(stats.overdue ?? 0) ? C.danger : C.dim} />
          <BigStat label="Сообщений сегодня" value={stats.messagesToday ?? 0} color={C.indigo} />
        </div>
        <Row label="Всего обращений" value={stats.conversations ?? 0} />
        <Row label="Всего сообщений" value={stats.messages ?? 0} />
      </Card>

      <Chips
        options={[
          { key: 'open', label: 'Открытые', count: by.open || 0 },
          { key: 'overdue', label: 'Просрочено', count: by.overdue || 0 },
          { key: 'closed', label: 'Закрытые', count: by.closed || 0 },
          { key: 'all', label: 'Все' },
        ]}
        value={status}
        onChange={v => { setStatus(v); setOpenUser(null) }}
      />

      <ErrorLine onRetry={list.reload}>{list.error}</ErrorLine>
      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      {list.loading && !rows.length ? <Loading /> : !rows.length ? <Empty>Диалогов нет</Empty> : (
        <Card style={{ padding: '6px 14px' }}>
          {rows.map((r, i) => {
            const open = openUser === r.user_id
            const waiting = r.lastSender === 'user' && r.status === 'open'
            const overdue = isOverdue(r)
            return (
              <div
                key={r.user_id}
                style={{
                  borderTop: i ? `1px solid ${C.line}` : 'none',
                  padding: '10px 0',
                  background: overdue ? 'rgba(248,113,113,0.04)' : 'none',
                  borderRadius: 8, margin: overdue ? '0 -4px' : 0, paddingLeft: overdue ? 4 : 0, paddingRight: overdue ? 4 : 0,
                }}
              >
                <div
                  onClick={() => { setOpenUser(open ? null : r.user_id); setText(''); setNote('') }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                      {userLabel(r)} <span style={{ color: C.dim, fontWeight: 400, fontSize: 11 }}>id {r.user_id}</span>
                    </div>
                    <div style={{
                      fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.4,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.lastSender === 'admin' ? 'Вы: ' : ''}{r.lastText || '—'}
                    </div>
                    <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
                      {fmtDateTime(r.last_message_at)} · сообщений {r.messages}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
                    {overdue && <Pill color={C.danger}>просрочено</Pill>}
                    {waiting && !overdue && <Pill color={C.warn}>ждёт</Pill>}
                    <Pill color={r.status === 'open' ? C.accent : C.dim}>{r.status === 'open' ? 'открыт' : 'закрыт'}</Pill>
                  </div>
                </div>

                {open && (
                  <div style={{ marginTop: 10 }}>
                    <ErrorLine onRetry={hist.reload}>{hist.error}</ErrorLine>
                    {hist.loading ? <Loading text="Загрузка переписки…" /> : (
                      <div style={{
                        background: '#0A0A0A', border: `1px solid ${C.line}`, borderRadius: 12,
                        padding: 10, maxHeight: 300, overflowY: 'auto', marginBottom: 10,
                      }}>
                        {!messages.length ? <Empty>Сообщений нет</Empty> : messages.map(m => {
                          const mine = m.sender === 'admin'
                          return (
                            <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                              <div style={{
                                maxWidth: '85%',
                                background: mine ? 'rgba(52,211,153,0.12)' : 'rgba(255,255,255,0.05)',
                                border: `1px solid ${mine ? 'rgba(52,211,153,0.25)' : C.line}`,
                                borderRadius: 12, padding: '7px 10px',
                              }}>
                                <div style={{ fontSize: 12, color: '#fff', lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                  {m.text}
                                </div>
                                <div style={{ fontSize: 9.5, color: C.dim, marginTop: 3, textAlign: 'right' }}>
                                  {mine ? 'вы' : 'юзер'} · {fmtDateTime(m.created_at)}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 8 }}>
                      {TEMPLATES.map(tpl => (
                        <button
                          key={tpl.key}
                          onClick={() => setText(tpl.text)}
                          style={{
                            flexShrink: 0, padding: '5px 10px', borderRadius: 8,
                            background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.line}`,
                            color: C.muted, fontSize: 11, cursor: 'pointer',
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          {tpl.label}
                        </button>
                      ))}
                    </div>

                    <textarea
                      value={text}
                      onChange={e => setText(e.target.value)}
                      rows={3}
                      maxLength={4000}
                      placeholder="Ответ юзеру…"
                      style={{ ...inputStyle, fontFamily: 'inherit', fontSize: 13, resize: 'vertical', lineHeight: 1.5 }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                      <Btn small icon={Send} disabled={busy || !text.trim()} onClick={send}>
                        {busy ? '…' : 'Отправить'}
                      </Btn>
                      {r.status === 'open' && (
                        <Btn small ghost color={C.muted} icon={CheckCircle2} disabled={busy} onClick={close}>Закрыть диалог</Btn>
                      )}
                      <div style={{ flex: 1, textAlign: 'right', fontSize: 10, color: C.dim }}>{text.length}/4000</div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          <div style={divider} />
          <div style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.45, paddingBottom: 8 }}>
            Ответ приходит юзеру в чат с ботом. Если бот запущен без BOT_TOKEN,
            сообщение сохранится в истории, но не уйдёт в Telegram.
          </div>
        </Card>
      )}
    </div>
  )
}
